import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { isPublishedPost } from "@circle-react/helpers/postHelpers";
import { userIsAdminOrModeratorOfSpace } from "@circle-react/helpers/spaceHelpers";
import type {
  ModalPrimaryActionProps,
  ModalSecondaryActionProps,
} from "@circleco/compass/components/Modal";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import type { UseFormSetValue, UseFormWatch } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import {
  usePostSubmitActionSelector,
  type PostSubmitActionOption,
} from "@/react/components/PostsCompass/PostSubmitActionSelector";
import { ScheduleDateTimePickerPopover } from "@/react/components/PostsCompass/ScheduleDateTimePickerPopover";
import { useImagePostModalStore } from "@/react/components/Spaces/ImageSpace/ImagePostModalCompass/store";
import type { ImagePost } from "@/react/types";

import { parsePublishedAt } from "./helpers";
import type { ImagePostFormValues } from "./types";

interface UseCreatePostActionsProps {
  onCancel: () => void;
  post: ImagePost;
  setValue: UseFormSetValue<ImagePostFormValues>;
  watch: UseFormWatch<ImagePostFormValues>;
}

interface CreatePostActions {
  footerProps: {
    primaryAction: ModalPrimaryActionProps;
    secondaryAction: ModalSecondaryActionProps;
  };
  supportContent: ReactNode;
}

export const useCreatePostActions = ({
  onCancel,
  post,
  setValue,
  watch,
}: UseCreatePostActionsProps): CreatePostActions => {
  const footerScheduleAnchorRef = useRef<HTMLDivElement>(null);
  const [isFooterSchedulePickerOpen, setIsFooterSchedulePickerOpen] =
    useState(false);
  const { isSubmitting, uploadedImages } = useImagePostModalStore();
  const { data: space } = useCurrentSpaceContext();
  const { published_at: publishedAt, status, topics } = watch();
  const scheduledAt = parsePublishedAt(publishedAt);
  const isTopicSelectionRequired =
    space?.require_topic_selection && (topics?.length ?? 0) <= 0;
  const isEdit = !!post?.id;
  const isPublished = isPublishedPost(post);
  const isSubmitEnabled =
    uploadedImages.length > 0 &&
    !isSubmitting &&
    !isTopicSelectionRequired &&
    (status !== "scheduled" || Boolean(scheduledAt));
  const disabledDays = useMemo(() => ({ before: new Date() }), []);

  const openSchedulePicker = useCallback(() => {
    setIsFooterSchedulePickerOpen(true);
  }, []);

  const updatePublishedAt = useCallback(
    (date?: Date | null) => {
      setValue("published_at", date?.toISOString() ?? null, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const setSelectedAction = useCallback(
    (action: PostSubmitActionOption) => {
      if (action.value === "scheduled") {
        openSchedulePicker();
        return;
      }

      setValue("status", action.value, { shouldDirty: true });
      setIsFooterSchedulePickerOpen(false);
      updatePublishedAt(null);
    },
    [openSchedulePicker, setValue, updatePublishedAt],
  );

  const { getModalPrimaryAction } = usePostSubmitActionSelector({
    isSubmitting,
    onSelectAction: setSelectedAction,
    status,
  });

  const onScheduleDateSave = (date: Date) => {
    updatePublishedAt(date);
    setValue("status", "scheduled", { shouldDirty: true });
  };

  const shouldShowActionSelector = Boolean(
    space && userIsAdminOrModeratorOfSpace(space) && !isPublished,
  );

  const hasScheduledDate = status === "scheduled" && Boolean(scheduledAt);
  const shouldRenderFooterSchedulePicker =
    shouldShowActionSelector && !hasScheduledDate;

  const supportContent = (
    <>
      {shouldShowActionSelector && (
        <div
          ref={footerScheduleAnchorRef}
          aria-hidden
          className="absolute bottom-4 right-6 size-px"
        />
      )}
      {shouldRenderFooterSchedulePicker && (
        <ScheduleDateTimePickerPopover
          anchor={footerScheduleAnchorRef}
          disabledDays={disabledDays}
          onOpenChange={setIsFooterSchedulePickerOpen}
          onSave={onScheduleDateSave}
          open={isFooterSchedulePickerOpen}
          value={scheduledAt}
        />
      )}
    </>
  );

  const footerProps: CreatePostActions["footerProps"] = {
    secondaryAction: {
      label: t("cancel"),
      onClick: onCancel,
      shouldCloseModal: false,
    },
    primaryAction: {
      ...getModalPrimaryAction({
        disabled: !isSubmitEnabled,
        isEdit,
        shouldShowActionSelector,
        type: "submit",
      }),
    },
  };

  return { footerProps, supportContent };
};
